import { Defined } from "@jutro/types";
import { unique } from "radash";
import { IpomFormSchema } from "components/IpomForm/schema";
import {
  DoctorLaboratoryExaminationsQuery,
  DoctorMSAssignment,
  DoctorMSIpomProcedureAssignmentInput,
  DoctorMSLaboratoryExaminationEntity,
  DoctorPatientMessagesQuery,
  DoctorProceduresQuery,
} from "lib/graphql/megaSchema";

const BASE_PROCEDURE_DATA = {
  type: "PROCEDURE",
  time: "NAJSZYB",
  timeUnit: null,
  timeValue: null,
  icd9: null,
  active: true,
} as const;

export const extractLabExams = (
  labExamsData: Defined<DoctorLaboratoryExaminationsQuery>,
  visitId: string,
) =>
  labExamsData.doctorLaboratoryExaminations
    .filter(({ createdOnVisitId }) => createdOnVisitId === visitId)
    .filter(
      ({ assignment }) => assignment.icd9,
    ) as DoctorMSLaboratoryExaminationEntity[];

const transformLabExams = (
  visitLabExams: DoctorMSLaboratoryExaminationEntity[],
): IpomFormSchema["scheduledDiagnosticTests"] => {
  return visitLabExams.map((labExam) => ({
    name: labExam.assignment.name,
    time: "NAJSZYB",
    timeUnit: null,
    timeValue: null,
    type: "LAB_EXAM",
    icd9: labExam.assignment.icd9,
    active: true,
  }));
};

export const extractSpirometryProcedure = (
  procedures: Defined<DoctorProceduresQuery>,
  visitId: string,
): IpomFormSchema["scheduledDiagnosticTests"] => {
  const visitSpirometryProcedures = procedures.doctorProcedures.filter(
    ({ createdOnVisitId, type }) =>
      createdOnVisitId === visitId && type === "SPIROMETRY",
  );

  if (visitSpirometryProcedures.length === 0) {
    return [];
  }

  const results: IpomFormSchema["scheduledDiagnosticTests"] = [];

  if (
    visitSpirometryProcedures.some(
      ({ spirometry }) => spirometry?.diastolicTest,
    )
  ) {
    results.push({
      name: "Spirometria z próbą rozkurczową",
      ...BASE_PROCEDURE_DATA,
    });
  }

  if (
    visitSpirometryProcedures.some(
      ({ spirometry }) => !spirometry?.diastolicTest,
    )
  ) {
    results.push({
      name: "Spirometria",
      ...BASE_PROCEDURE_DATA,
    });
  }

  return results;
};

export const filterUniqueLabExams = (
  existingLabExams: IpomFormSchema["scheduledDiagnosticTests"],
  visitSpirometryProcedures: IpomFormSchema["scheduledDiagnosticTests"],
  visitLabExams: DoctorMSLaboratoryExaminationEntity[],
) => {
  return unique(
    [
      ...existingLabExams,
      ...visitSpirometryProcedures,
      ...transformLabExams(visitLabExams),
    ],
    (labExam) => labExam.name,
  );
};

export const extractUsg = (
  patientMessagesData: Defined<DoctorPatientMessagesQuery>,
  visitId: string,
) => {
  const usgAndRtg = (
    patientMessagesData.doctorGetMessagesForPatient.filter(
      (message) =>
        message.__typename === "DoctorMSAssignment" &&
        message.visit.id === visitId &&
        message.assignmentType === "USG",
    ) as DoctorMSAssignment[]
  ).flatMap(({ positions }) => positions);

  const preparedUsgToIpom = usgAndRtg.map(
    (assigment) =>
      ({
        ...BASE_PROCEDURE_DATA,
        name: assigment?.serviceName ?? "",
        type: "IMG",
        // if type is IMG backend finds icd9 by serviceId
        icd9: assigment?.serviceId ?? "",
      }) as const,
  );

  return preparedUsgToIpom;
};

export const extractRtg = (
  patientMessagesData: Defined<DoctorPatientMessagesQuery>,
  visitId: string,
) => {
  const usgAndRtg = (
    patientMessagesData.doctorGetMessagesForPatient.filter(
      (message) =>
        message.__typename === "DoctorMSAssignment" &&
        message.visit.id === visitId &&
        message.assignmentType === "RTG",
    ) as DoctorMSAssignment[]
  ).flatMap(({ positions }) => positions);

  const preparedUsgToIpom = usgAndRtg.map(
    (assigment) =>
      ({
        ...BASE_PROCEDURE_DATA,
        name: assigment?.serviceName ?? "",
        type: "IMG",
        // if type is IMG backend finds icd9 by serviceId
        icd9: assigment?.serviceId ?? "",
      }) as const,
  );

  return preparedUsgToIpom;
};

export const extractCardiologyProcedures = (
  patientMessagesData: Defined<DoctorPatientMessagesQuery>,
  visitId: string,
) => {
  const referrals = (
    patientMessagesData.doctorGetMessagesForPatient.filter(
      (message) =>
        message.__typename === "DoctorMSIpom" &&
        message.visitId === visitId &&
        message.assignmentType === "cardiology-diagnostic",
    ) as DoctorMSIpomProcedureAssignmentInput[]
  ).flatMap(({ data }) => data.icd9);

  const preparedReferralsToIpom = referrals.map(
    (referral) =>
      ({
        ...BASE_PROCEDURE_DATA,
        name: referral?.name ?? "",
        type: "CARDIOLOGY_PROCEDURE",
        icd9: referral?.icd9 ?? "",
      }) as const,
  );

  return preparedReferralsToIpom;
};
